import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AjaxService } from 'src/app/service/ajax.service';
import { UtilService } from 'src/app/service/util.service';
import { API } from 'src/environments/environment';

interface OrcamentoItem {
  idOrcamento: number;
  idConsulta: number;
  titulo: string;
  data: string;
  observacao: string;
  desconto: number;
  acrescimo: number;
}

@Component({
  selector: 'app-orcamentos-consulta',
  templateUrl: './orcamentos-consulta.component.html',
  styleUrls: ['./orcamentos-consulta.component.scss']
})
export class OrcamentosConsultaComponent implements OnInit {
  idConsulta = '';
  loading = false;
  orcamentos: OrcamentoItem[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ajaxService: AjaxService,
    private util: UtilService
  ) { }

  ngOnInit(): void {
    this.idConsulta =
      this.route.snapshot.queryParamMap.get('idConsulta')
      || this.route.snapshot.queryParamMap.get('id_consulta')
      || '';
    if (!this.idConsulta) {
      this.util.toasterShowError('Atenção', 'Consulta inválida para listar orçamentos.');
      return;
    }
    this.carregarOrcamentos();
  }

  carregarOrcamentos(): void {
    this.loading = true;
    this.util.showLoading();
    this.ajaxService
      .get(`${API}empresa/Consulta/ListarOrcamentos?idConsulta=${encodeURIComponent(this.idConsulta)}`)
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.util.closeLoading();
          if (response?.hasErrors) {
            this.util.toasterShowError('Erro', response?.message || 'Erro ao listar orçamentos.');
            this.orcamentos = [];
            return;
          }

          const lista = Array.isArray(response?.data?.orcamentos) ? response.data.orcamentos : [];
          this.orcamentos = lista.map((x: any) => {
            const item = this.util.converterParaLowercase(x || {});
            return {
              idOrcamento: Number(item.idorcamento || 0),
              idConsulta: Number(item.idconsulta || 0),
              titulo: item.titulo || '',
              data: item.data || '',
              observacao: item.observacao || '',
              desconto: Number(item.desconto || 0),
              acrescimo: Number(item.acrescimo || 0)
            } as OrcamentoItem;
          });
        },
        error: () => {
          this.loading = false;
          this.util.closeLoading();
          this.orcamentos = [];
          this.util.toasterShowError('Erro', 'Erro ao listar orçamentos.');
        }
      });
  }

  novoOrcamento(idConsultaOrigem?: number | string): void {
    const idConsulta =
      String(idConsultaOrigem || '').trim()
      || this.idConsulta
      || this.route.snapshot.queryParamMap.get('idConsulta')
      || this.route.snapshot.queryParamMap.get('id_consulta')
      || '';

    if (!idConsulta) {
      this.util.toasterShowError('Atenção', 'Consulta inválida para criar orçamento.');
      return;
    }

    this.router.navigate(['/novo-orcamento-consulta'], {
      queryParams: {
        idConsulta
      }
    });
  }

  excluir(item: OrcamentoItem): void {
    if (!item?.idOrcamento) return;

    this.loading = true;
    this.util.showLoading();
    this.ajaxService
      .post(`${API}empresa/Consulta/DeletarOrcamento`, { id_orcamento: String(item.idOrcamento) })
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.util.closeLoading();
          if (response?.hasErrors) {
            this.util.toasterShowError('Erro', response?.message || 'Não foi possível excluir o orçamento.');
            return;
          }
          this.util.toasterShowSuccess('Sucesso', 'Orçamento excluído com sucesso.');
          this.carregarOrcamentos();
        },
        error: () => {
          this.loading = false;
          this.util.closeLoading();
          this.util.toasterShowError('Erro', 'Erro ao excluir orçamento.');
        }
      });
  }

  visualizar(item: OrcamentoItem): void {
    if (!item?.idOrcamento) {
      this.util.toasterShowError('Atenção', 'Orçamento inválido para visualização.');
      return;
    }

    this.router.navigate(['/novo-orcamento-consulta'], {
      queryParams: {
        idConsulta: String(item.idConsulta || this.idConsulta),
        idOrcamento: String(item.idOrcamento)
      }
    });
  }

  imprimir(item: OrcamentoItem): void {
    if (!item?.idOrcamento) {
      this.util.toasterShowError('Atenção', 'Orçamento inválido para impressão.');
      return;
    }

    this.util.showLoading();
    this.ajaxService
      .get(`${API}empresa/Consulta/ObterOrcamento?idOrcamento=${encodeURIComponent(String(item.idOrcamento))}`)
      .subscribe({
        next: (response) => {
          this.util.closeLoading();
          if (response?.hasErrors) {
            this.util.toasterShowError('Erro', response?.message || 'Não foi possível carregar dados para impressão.');
            return;
          }

          const data = this.util.converterParaLowercase(response?.data || {});
          const orcamento = this.util.converterParaLowercase(data?.orcamento || {});
          const itens = Array.isArray(data?.orcamentoitens) ? data.orcamentoitens.map((x: any) => this.util.converterParaLowercase(x)) : [];

          const linhas = itens.map((x: any) => `
            <tr>
              <td>${x.etapa || '-'}</td>
              <td>R$ ${Number(x.totagrupo || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              <td>R$ ${Number(x.parcelacadamenor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
              <td>R$ ${Number(x.parcelacadamaior || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
            </tr>
          `).join('');

          const html = `
            <html>
              <head>
                <title>Imprimir Orçamento</title>
                <style>
                  body { font-family: Arial, sans-serif; padding: 20px; color: #222; }
                  h2 { margin-bottom: 8px; }
                  .meta { margin-bottom: 16px; }
                  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
                  th, td { border: 1px solid #ddd; padding: 8px; font-size: 12px; }
                  th { background: #f4f6f8; text-align: left; }
                </style>
              </head>
              <body>
                <h2>${orcamento?.titulo || 'Orçamento'}</h2>
                <div class="meta"><strong>Tribunal:</strong> ${orcamento?.tribunal || '-'}<br/><strong>Data:</strong> ${orcamento?.data ? new Date(orcamento.data).toLocaleDateString('pt-BR') : '-'}</div>
                <div><strong>Observação:</strong> ${orcamento?.observacaogeral || '-'}</div>
                <table>
                  <thead>
                    <tr>
                      <th>Etapa</th>
                      <th>Parcela Total Grupo</th>
                      <th>Parcela de Cada Menor</th>
                      <th>Parcela de Cada Maior</th>
                    </tr>
                  </thead>
                  <tbody>${linhas}</tbody>
                </table>
              </body>
            </html>
          `;

          const popup = window.open('', '_blank');
          if (!popup) {
            this.util.toasterShowError('Erro', 'Não foi possível abrir a janela de impressão.');
            return;
          }
          popup.document.open();
          popup.document.write(html);
          popup.document.close();
          setTimeout(() => {
            popup.focus();
            popup.print();
          }, 300);
        },
        error: () => {
          this.util.closeLoading();
          this.util.toasterShowError('Erro', 'Erro ao carregar orçamento para impressão.');
        }
      });
  }

  voltar(): void {
    this.router.navigate(['/consultar-atendimento']);
  }
}
